import { Banner, BarChart, Card } from '@servicecompressionllc/sc-design-system';
import {
  formatCompact,
  formatCount,
  formatUsdBrief,
  formatDayKeyShort,
  formatPercent,
  formatRate,
  formatUsdWhole,
} from '../../lib/format.js';
import type { UsageSummaryResponse } from '../../lib/types.js';
import { SOURCE_GROUPS, modelName, sourceGroupOf, type SourceGroupName } from './usageLabels.js';

type Spend = UsageSummaryResponse['spend'];

const cardTitle = 'text-sm font-semibold text-ink';
const cardNote = 'text-xs text-ink-subtle';

const OTHER = { name: 'Other' as const, color: 'var(--ink-subtle)' };

export const SpendBySourceCard = ({ spend }: { spend: Spend }) => {
  const totals = new Map<SourceGroupName, number>();
  const byDay = new Map<string, Record<string, number | string>>();
  for (const d of spend.bySourcePerDay) {
    const group = sourceGroupOf(d.source);
    totals.set(group, (totals.get(group) ?? 0) + d.costUsd);
    const row = byDay.get(d.date) ?? { day: formatDayKeyShort(d.date) };
    row[group] = Number(row[group] ?? 0) + d.costUsd;
    byDay.set(d.date, row);
  }
  const groups = [...SOURCE_GROUPS, OTHER].filter((g) => (totals.get(g.name) ?? 0) > 0);
  const rows = [...byDay.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, row]) => ({ ...Object.fromEntries(groups.map((g) => [g.name, 0])), ...row }));
  return (
    <Card padding="md">
      <div className="flex min-w-0 flex-col gap-3.5">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 className={cardTitle}>Spend per day, by source</h3>
          <span className={cardNote}>Estimated, USD</span>
        </div>
        <BarChart
          data={rows}
          xKey="day"
          yKey="day"
          series={groups.map((g) => ({
            key: g.name,
            label: `${g.name} · ${formatUsdBrief(totals.get(g.name) ?? 0)}`,
            fill: g.color,
          }))}
          height={260}
          legendPosition="bottom"
          formatValue={formatUsdBrief}
          ariaLabel="Estimated spend per day, stacked by source"
          emptyMessage="No spend in this period."
        />
      </div>
    </Card>
  );
};

export const SpendByModelCard = ({ spend }: { spend: Spend }) => {
  const total = spend.byModel.reduce((n, m) => n + m.costUsd, 0);
  return (
    <Card padding="md">
      <div className="flex min-w-0 flex-col gap-3.5">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 className={cardTitle}>Spend by model</h3>
          <span className={cardNote}>List price per 1M tokens: in / out / cache read</span>
        </div>
        {spend.byModel.length === 0 ? (
          <p className="text-sm text-ink-muted">No spend in this period.</p>
        ) : (
          <ul className="flex flex-col gap-3.5">
            {spend.byModel.map((m) => (
              <li key={m.model} className="flex flex-col gap-1.5">
                <div className="flex justify-between gap-2">
                  <span className="font-medium text-ink">{modelName(m.model)}</span>
                  <span className="tabular-nums text-ink">{formatUsdBrief(m.costUsd)}</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-surface" aria-hidden="true">
                  <div
                    className="h-2 rounded-full bg-chart-1"
                    style={{ width: `${String(total > 0 ? (m.costUsd / total) * 100 : 0)}%` }}
                  />
                </div>
                <span className={cardNote}>
                  {m.pricing
                    ? `${formatRate(m.pricing.inputPerMTokUsd)} / ${formatRate(m.pricing.outputPerMTokUsd)} / ${formatRate(m.pricing.cacheReadPerMTokUsd)}`
                    : 'No list price, so not counted in spend'}
                  {` · ${formatCount(m.calls)} ${m.calls === 1 ? 'call' : 'calls'}`}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
};

/** Cache writes above this share of spend earn the callout. */
const WRITE_SHARE_CALLOUT = 0.25;

export const MakeupCard = ({ spend }: { spend: Spend }) => {
  const { makeup } = spend;
  const parts = [
    {
      name: 'Fresh input',
      color: 'var(--chart-3)',
      usd: makeup.freshInputUsd,
      tokens: makeup.tokens.freshInput,
    },
    {
      name: 'Cache writes',
      color: 'var(--chart-2)',
      usd: makeup.cacheWriteUsd,
      tokens: makeup.tokens.cacheWrite,
    },
    {
      name: 'Cache reads',
      color: 'var(--chart-1)',
      usd: makeup.cacheReadUsd,
      tokens: makeup.tokens.cacheRead,
    },
    {
      name: 'Output',
      color: 'var(--chart-4)',
      usd: makeup.outputUsd,
      tokens: makeup.tokens.output,
    },
  ];
  const total = parts.reduce((n, p) => n + p.usd, 0);
  const share = (usd: number) => (total > 0 ? usd / total : 0);
  const writeShare = share(makeup.cacheWriteUsd);
  return (
    <Card padding="md">
      <div className="flex flex-col gap-3.5">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 className={cardTitle}>What the spend is made of</h3>
          <span className={cardNote}>
            Cache reads cost a small fraction of fresh input; cache writes cost twice as much
          </span>
        </div>
        <div
          className="flex h-7 overflow-hidden rounded"
          role="img"
          aria-label="Spend by kind of token"
        >
          {parts.map((p) => (
            <div
              key={p.name}
              title={p.name}
              style={{ width: `${String(share(p.usd) * 100)}%`, background: p.color }}
            />
          ))}
        </div>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(10.625rem,1fr))] gap-3">
          {parts.map((p) => (
            <div key={p.name} className="flex flex-col gap-0.5">
              <span className="flex items-center gap-1.5 text-xs text-ink-muted">
                <span className="h-2.5 w-2.5 rounded-sm" style={{ background: p.color }} />
                {p.name}
              </span>
              <span className="text-lg font-semibold tabular-nums text-ink">
                {formatUsdWhole(p.usd)}{' '}
                <span className="text-xs font-normal text-ink-subtle">
                  {formatPercent(share(p.usd))}
                </span>
              </span>
              <span className={cardNote}>{formatCompact(p.tokens)} tokens</span>
            </div>
          ))}
        </div>
        {writeShare >= WRITE_SHARE_CALLOUT && (
          <Banner variant="info" role="status">
            <Banner.Body>
              <Banner.Title>Cache writes are {formatPercent(writeShare)} of spend.</Banner.Title>
              <Banner.Description>
                Long conversations rewrite their cached history. The most expensive conversations
                above show which ones.
              </Banner.Description>
            </Banner.Body>
          </Banner>
        )}
      </div>
    </Card>
  );
};
